import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface ChatbotSettings {
  welcome_message: string;
  is_enabled: boolean;
}

export const useChatbot = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [settings, setSettings] = useState<ChatbotSettings | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem("chat_session_id");
    if (stored) return stored;
    const newId = crypto.randomUUID();
    localStorage.setItem("chat_session_id", newId);
    return newId;
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase
        .from("chatbot_settings")
        .select("welcome_message, is_enabled")
        .single();

      if (data) {
        setSettings(data);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    if (isOpen && messages.length === 0 && settings?.welcome_message) {
      setMessages([
        {
          id: "welcome",
          role: "assistant",
          content: settings.welcome_message,
          timestamp: new Date(),
        },
      ]);

      // Track chat opened event
      trackAnalyticsEvent("chat_opened", {});
    }
  }, [isOpen, settings?.welcome_message, messages.length]);

  // Track analytics event
  const trackAnalyticsEvent = useCallback(
    async (eventType: string, eventData: Record<string, unknown>) => {
      try {
        await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-assistant`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            },
            body: JSON.stringify({
              sessionId,
              conversationId,
              analyticsEvent: {
                type: eventType,
                data: eventData,
              },
            }),
          }
        );
      } catch (error) {
        // Silently fail analytics - don't disrupt user experience
        console.debug("Analytics tracking failed:", error);
      }
    },
    [sessionId, conversationId]
  );

  // Public method to track quick reply clicks
  const trackQuickReplyClick = useCallback(
    (label: string, message: string) => {
      trackAnalyticsEvent("quick_reply_click", { label, message });
    },
    [trackAnalyticsEvent]
  );

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading || isBlocked) return;

      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: "user",
        content: content.trim(),
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const assistantMessageId = crypto.randomUUID();
      let assistantContent = "";

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-assistant`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            },
            body: JSON.stringify({
              message: content.trim(),
              conversationId,
              sessionId,
            }),
            signal: abortControllerRef.current.signal,
          }
        );

        // Get conversation ID from response headers
        const newConversationId = response.headers.get("X-Conversation-Id");
        if (newConversationId && !conversationId) {
          setConversationId(newConversationId);
        }

        if (!response.ok) {
          const errorData = await response.json();
          
          // Handle blocked session
          if (errorData.blocked) {
            setIsBlocked(true);
            throw new Error(errorData.error);
          }
          
          // Handle rate limit
          if (errorData.rateLimit || errorData.limitReached) {
            throw new Error(errorData.error);
          }
          
          throw new Error(errorData.error || "Failed to send message");
        }

        if (!response.body) {
          throw new Error("No response body");
        }

        // Add empty assistant message to update
        setMessages((prev) => [
          ...prev,
          { id: assistantMessageId, role: "assistant", content: "", timestamp: new Date() },
        ]);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Process complete lines
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ") && line !== "data: [DONE]") {
              try {
                const json = JSON.parse(line.slice(6));
                const delta = json.choices?.[0]?.delta?.content;
                if (delta) {
                  assistantContent += delta;
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === assistantMessageId
                        ? { ...msg, content: assistantContent }
                        : msg
                    )
                  );
                }
              } catch {
                // Ignore parse errors for incomplete JSON
              }
            }
          }
        }
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          return;
        }

        console.error("Chat error:", error);
        setMessages((prev) => [
          ...prev.filter((msg) => msg.id !== assistantMessageId),
          {
            id: assistantMessageId,
            role: "assistant",
            content:
              (error as Error).message ||
              "I apologize, but I'm having trouble responding right now. Please try again or contact us directly.",
            timestamp: new Date(),
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [conversationId, sessionId, isLoading, isBlocked]
  );

  const clearChat = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setIsBlocked(false);
    if (settings?.welcome_message) {
      setMessages([
        {
          id: "welcome",
          role: "assistant",
          content: settings.welcome_message,
          timestamp: new Date(),
        },
      ]);
    }
  }, [settings?.welcome_message]);

  return {
    messages,
    isLoading,
    isOpen,
    setIsOpen,
    sendMessage,
    clearChat,
    isEnabled: settings?.is_enabled ?? false,
    isBlocked,
    trackQuickReplyClick,
  };
};
